/*
 * Top page hero: a looping row of article slides, the current one centred.
 *
 * The slides are copied once before and once after the real ones, so moving
 * past either end lands on a copy; after the move the row jumps back to the
 * matching real slide without animation, which reads as an endless loop.
 * Autoplays every data-interval seconds (0 turns it off, as does reduced
 * motion), pauses while hovered or focused, and follows swipes and drags.
 */
const DRAG_THRESHOLD = 50;

for (const viewport of document.querySelectorAll('[data-hero-slider]')) {
  const track = viewport.querySelector('[data-hero-track]');
  if (!(viewport instanceof HTMLElement) || !(track instanceof HTMLElement) || viewport.dataset.bound) continue;
  viewport.dataset.bound = 'true';

  const originals = Array.from(track.children);
  const count = originals.length;
  if (count < 2) continue;

  const copy = (slide) => {
    const clone = slide.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    for (const image of clone.querySelectorAll('img')) image.loading = 'lazy';
    return clone;
  };
  track.prepend(...originals.map(copy));
  track.append(...originals.map(copy));
  viewport.classList.add('is-ready');

  const slides = Array.from(track.children);
  let index = count;
  let offset = 0;
  let busy = false;

  const centreOf = (i) => {
    const slide = slides[i];
    return slide.offsetLeft + slide.offsetWidth / 2 - viewport.clientWidth / 2;
  };

  const place = (animate) => {
    offset = centreOf(index);
    track.style.transition = animate ? '' : 'none';
    track.classList.toggle('is-instant', !animate);
    track.style.transform = `translate3d(${-offset}px, 0, 0)`;
    slides.forEach((slide, i) => slide.classList.toggle('is-current', i === index));
    // Applies an unanimated jump now, so the next move animates from it.
    if (!animate) void track.offsetWidth;
  };

  let settleTimer = 0;
  const settle = () => {
    window.clearTimeout(settleTimer);
    if (!busy) return;
    if (index >= count * 2) index -= count;
    if (index < count) index += count;
    place(false);
    busy = false;
  };

  const go = (step) => {
    if (busy) return;
    busy = true;
    index += step;
    place(true);
    // In case the transition is cut short and never reports its end.
    settleTimer = window.setTimeout(settle, 1000);
  };

  track.addEventListener('transitionend', (event) => {
    if (event.target === track) settle();
  });

  // Autoplay
  const seconds = Number(viewport.dataset.interval) || 0;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let timer = 0;
  let paused = false;
  const stop = () => window.clearInterval(timer);
  const start = () => {
    stop();
    if (seconds > 0 && !reduced && !paused) timer = window.setInterval(() => go(1), seconds * 1000);
  };
  const pause = (value) => {
    paused = value;
    start();
  };
  // An arrow press restarts the autoplay count, so the two never stack.
  viewport.querySelector('[data-hero-prev]')?.addEventListener('click', () => {
    go(-1);
    start();
  });
  viewport.querySelector('[data-hero-next]')?.addEventListener('click', () => {
    go(1);
    start();
  });
  viewport.addEventListener('mouseenter', () => pause(true));
  viewport.addEventListener('mouseleave', () => pause(false));
  viewport.addEventListener('focusin', () => pause(true));
  viewport.addEventListener('focusout', () => pause(false));
  document.addEventListener('visibilitychange', () => (document.hidden ? stop() : start()));

  // Swipe and drag
  let startX = 0;
  let deltaX = 0;
  let dragging = false;
  let moved = false;
  viewport.addEventListener('pointerdown', (event) => {
    if (busy || event.button !== 0 || event.target.closest('button')) return;
    dragging = true;
    moved = false;
    startX = event.clientX;
    deltaX = 0;
    track.style.transition = 'none';
    void track.offsetWidth;
    stop();
  });
  viewport.addEventListener('pointermove', (event) => {
    if (!dragging) return;
    deltaX = event.clientX - startX;
    if (Math.abs(deltaX) > 5 && !moved) {
      moved = true;
      viewport.setPointerCapture(event.pointerId);
    }
    track.style.transform = `translate3d(${-offset + deltaX}px, 0, 0)`;
  });
  const release = () => {
    if (!dragging) return;
    dragging = false;
    if (deltaX <= -DRAG_THRESHOLD) go(1);
    else if (deltaX >= DRAG_THRESHOLD) go(-1);
    else place(true);
    start();
  };
  viewport.addEventListener('pointerup', release);
  viewport.addEventListener('pointercancel', release);
  // A drag is not a click on the slide under the pointer.
  viewport.addEventListener(
    'click',
    (event) => {
      if (!moved) return;
      event.preventDefault();
      event.stopPropagation();
      moved = false;
    },
    true
  );
  for (const link of track.querySelectorAll('a, img')) link.setAttribute('draggable', 'false');

  window.addEventListener('resize', () => place(false));
  place(false);
  start();
}
